/* eslint-disable indent */
/*
 *
 * Email Notification Component
 *
 */

import React, { useState } from "react";
import { bool, object, number, func, string, oneOfType } from "prop-types";
import { Row, Col, Alert } from "react-bootstrap";
import Select from "react-select-plus";
import "react-select-plus/dist/react-select-plus.css";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import { websiteUrl } from "utils/env";
import { getFormattedTimeZoneText } from "utils/helperFunctions";
import clockIcon from "resources/images/clock-2x.png";
import { timeZoneList } from "containers/App/constants";
import {
  emailHoursDelayOptions,
  getEmailTemplateOptions,
  getUpgradeIcon,
} from "./helpers";
import EmailPreviewBox from "./EmailPreviewBox";
import SMSPreviewBox from "./SMSPreviewBox";
import ViewPreviewButton from "./ViewPreviewButton";

const Notifications = (props) => {
  const {
    notifyEmail,
    emailTemplates,
    emailTemplateId,
    handleChangeEmailTemplateId,
    handleChangeNotifyEmail,
    vhs,
    hoursDelay,
    handleSelectHoursDelay,
    companyName,
    isInsightUser,
    handleChangeNotifySMS,
    notifySMS,
    isSMSOn,
    fromScheduleExistingCandidates,
    job,
    companyDefaultTimezone,
    isAU,
    isInternal,
    proctoring,
    showProctoring
  } = props;

  const [viewEmailPreview, setViewEmailPreview] = useState(false);
  const [viewSMSPreview, setViewSMSPreview] = useState(false);

  let selectedEmailTemplate = null;
  if (
    Number(emailTemplateId) !== 0 &&
    emailTemplates &&
    emailTemplates.size > 0
  ) {
    selectedEmailTemplate = emailTemplates.get(String(emailTemplateId));
  }

  const emailTemplateValue =
    emailTemplateId && emailTemplates.get(String(emailTemplateId))
      ? emailTemplateId
      : 0;

  const showProUpgrades = vhs === 1;

  const showStandardUpgrades = vhs < 1;

  const openLearnMore = () => {
    window.open(`${websiteUrl}versionsInfo`, "_blank");
  };

  const showSMSToggle =
    !fromScheduleExistingCandidates && notifyEmail && isSMSOn;

  const showSMSContent = isSMSOn && notifySMS;

  const candidateInvitationTimeZone =
    job && job.get("candidateInvitationTimeZone");

  const timezoneText = getFormattedTimeZoneText(
    candidateInvitationTimeZone,
    timeZoneList,
    isAU,
    companyDefaultTimezone,
    false
  );

  const notifySMSToggleStyle = notifySMS
    ? { marginBottom: "8px" }
    : { marginBottom: "32px" };

  const smsAddOnText = showSMSContent ? "and SMS " : "";

  return (
    <Row className="notifications-info-box mx-0 p-4">
      <Col className="px-0">
        <h4 className="mb-3">Notifications</h4>
        <Row>
          <Col>
            <div className="d-flex align-items-center react-toggle-set-small">
              <Toggle
                icons={false}
                id="notify-candidate-email"
                className="global-toggle react-toggle-small"
                style={{ marginLeft: "-7px" }}
                checked={notifyEmail}
                onChange={handleChangeNotifyEmail}
              />
              <span className="react-toggle-label">
                Do you want to invite candidates by email?
              </span>
            </div>
            {notifyEmail ? (
              <div className="mb-2" style={{ display: "flex" }}>
                <Select
                  name="notifyEmail"
                  id="notifyEmail"
                  className="my-auto"
                  value={emailTemplateValue}
                  options={getEmailTemplateOptions(emailTemplates)}
                  onChange={handleChangeEmailTemplateId}
                  clearable={false}
                  style={{ width: "250px" }}
                  disabled={showStandardUpgrades || showProUpgrades}
                />
                {getUpgradeIcon(
                  showStandardUpgrades,
                  showProUpgrades,
                  openLearnMore
                )}
                <div className="m-3">
                  <ViewPreviewButton
                    setViewPreview={setViewEmailPreview}
                    viewPreview={viewEmailPreview}
                    notificationType="Email"
                  />
                </div>
              </div>
            ) : null}
          </Col>
          {isInsightUser ? (
            <Col>
              <Alert variant="info">
                <strong>IMPORTANT NOTICE:</strong> In order for email templates
                to appear in the dropdown, they must include the Candidate
                Testing Link. You can add this link by going to Manage Tab {">"}{" "}
                Email Templates. Select the template you would like to edit and
                click the "..." button and then Edit Template.
              </Alert>
            </Col>
          ) : null}
        </Row>
        {viewEmailPreview && notifyEmail && (
          <EmailPreviewBox
            companyName={companyName}
            defaultEmailTemplateType="invitation"
            emailToPreview={selectedEmailTemplate}
            isInsightUser={isInsightUser}
            proctoring={proctoring}
            showProctoring={showProctoring}
          />
        )}
        {showSMSToggle ? (
          <div
            className="d-flex align-items-center react-toggle-set-small"
            style={notifySMSToggleStyle}
          >
            <Toggle
              icons={false}
              id="notify-candidate-sms"
              className="global-toggle react-toggle-small"
              checked={notifySMS}
              onChange={handleChangeNotifySMS}
              disabled={isInternal}
            />
            <span className="react-toggle-label">
              Do you want to invite candidates by SMS?
            </span>
          </div>
        ) : null}
        {showSMSContent ? (
          <>
            <p className="mb-2" style={{ fontSize: "12px" }}>
              {`You certify that you have the candidate's consent to send them
              text messages. Message and data rates may apply.`}
            </p>
            <div className="my-2">
              <ViewPreviewButton
                setViewPreview={setViewSMSPreview}
                viewPreview={viewSMSPreview}
                notificationType="SMS"
              />
            </div>
            {viewSMSPreview && <SMSPreviewBox />}
            <p
              style={{
                color: "#53565a",
                fontSize: "12px",
                marginBottom: "32px",
              }}
            >
              {`A reminder SMS will also be sent at 10 AM ${timezoneText} 2 days after
              the candidate is invited or if expiration is set, 10 AM ${timezoneText} the day before. To set an email reminder, proceed to
              advanced settings.`}
            </p>
          </>
        ) : null}
        {notifyEmail && (
          <div
            className="my-auto p-3"
            style={{
              display: "flex",
              backgroundColor: "#fff",
              borderRadius: "4px",
            }}
          >
            <img
              src={clockIcon}
              height="16"
              width="16"
              alt="clock"
              className="mr-1 my-auto"
            />
            <div className="pl-1 mr-2 my-auto" style={{ lineHeight: "16px" }}>
              Send Email {smsAddOnText}Invitation
            </div>
            <Select
              name="emailHoursDelay"
              className="emailHoursDelaySelect ml-1"
              id="emailHoursDelay"
              value={hoursDelay}
              options={emailHoursDelayOptions}
              onChange={handleSelectHoursDelay}
              clearable={false}
              style={{ width: "150px" }}
            />
          </div>
        )}
      </Col>
    </Row>
  );
};

Notifications.propTypes = {
  notifyEmail: bool,
  emailTemplates: object,
  emailTemplateId: oneOfType([number, string]),
  handleChangeEmailTemplateId: func,
  handleChangeNotifyEmail: func,
  vhs: number,
  hoursDelay: number,
  handleSelectHoursDelay: func,
  companyName: string,
  isInsightUser: bool,
  handleChangeNotifySMS: func,
  notifySMS: bool,
  isSMSOn: bool,
  fromScheduleExistingCandidates: bool,
  job: object,
  companyDefaultTimezone: string,
  isAU: bool,
  isInternal: bool,
  proctoring: bool,
  showProctoring: bool,
};

export default Notifications;
